import { createContext, useContext, useState, useEffect } from "react";
import api from "../lib/api";

const ThemeContext = createContext(null);

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error("useTheme must be used within ThemeProvider");
  }
  return context;
};

const normalizeThemeId = (themeId) => {
  if (themeId === "blackhole") {
    return "nebulaflow";
  }
  return themeId || "default";
};

// 10 Logo options
export const logoOptions = [
  {
    id: "default",
    name: "Varsayılan",
    svg: `<svg viewBox="0 0 40 40"><path d="M8 8 L20 8 Q28 8 28 16 Q28 22 22 24 L30 32 L24 32 L16 24 L14 24 L14 32 L8 32 Z M14 14 L14 20 L18 20 Q22 20 22 17 Q22 14 18 14 Z" fill="currentColor"/><circle cx="32" cy="10" r="4" fill="currentColor" opacity="0.8"/></svg>`
  },
  {
    id: "minimal",
    name: "Minimal",
    svg: `<svg viewBox="0 0 40 40"><rect x="8" y="8" width="24" height="24" rx="4" fill="none" stroke="currentColor" stroke-width="3"/><text x="20" y="26" text-anchor="middle" font-size="16" font-weight="bold" fill="currentColor">R</text></svg>`
  },
  {
    id: "cosmic",
    name: "Kozmik",
    svg: `<svg viewBox="0 0 40 40" xmlns="http://www.w3.org/2000/svg">
      <defs>
        <radialGradient id="cosmicCore" cx="50%" cy="50%">
          <stop offset="0%" style="stop-color:#ffffff;stop-opacity:1" />
          <stop offset="30%" style="stop-color:#00d4ff;stop-opacity:0.9" />
          <stop offset="70%" style="stop-color:#7000ff;stop-opacity:0.6" />
          <stop offset="100%" style="stop-color:#000000;stop-opacity:0" />
        </radialGradient>
        <filter id="cosmicGlow">
          <feGaussianBlur stdDeviation="2" result="blur"/>
          <feComposite in="SourceGraphic" in2="blur" operator="over"/>
        </filter>
      </defs>
      
      <!-- Outer orbit ring -->
      <circle cx="20" cy="20" r="16" fill="none" stroke="url(#cosmicCore)" stroke-width="0.3" opacity="0.4">
        <animate attributeName="stroke-dasharray" values="100,0;0,100;100,0" dur="8s" repeatCount="indefinite"/>
      </circle>
      
      <!-- Middle orbit ring -->
      <circle cx="20" cy="20" r="12" fill="none" stroke="#00d4ff" stroke-width="0.4" opacity="0.5" stroke-dasharray="4,4">
        <animateTransform attributeName="transform" type="rotate" from="0 20 20" to="360 20 20" dur="6s" repeatCount="indefinite"/>
      </circle>
      
      <!-- Inner orbit ring -->
      <circle cx="20" cy="20" r="8" fill="none" stroke="#7000ff" stroke-width="0.5" opacity="0.6" stroke-dasharray="2,2">
        <animateTransform attributeName="transform" type="rotate" from="360 20 20" to="0 20 20" dur="4s" repeatCount="indefinite"/>
      </circle>
      
      <!-- Core sphere with gradient -->
      <circle cx="20" cy="20" r="6" fill="url(#cosmicCore)" filter="url(#cosmicGlow)">
        <animate attributeName="r" values="5.5;6.5;5.5" dur="2s" repeatCount="indefinite"/>
      </circle>
      
      <!-- Orbiting particles -->
      <circle cx="32" cy="20" r="1.2" fill="#00d4ff" filter="url(#cosmicGlow)">
        <animateTransform attributeName="transform" type="rotate" from="0 20 20" to="360 20 20" dur="3s" repeatCount="indefinite"/>
      </circle>
      <circle cx="8" cy="20" r="1" fill="#7000ff" opacity="0.8">
        <animateTransform attributeName="transform" type="rotate" from="180 20 20" to="540 20 20" dur="3s" repeatCount="indefinite"/>
      </circle>
      <circle cx="20" cy="8" r="0.8" fill="#ff00ff" opacity="0.7">
        <animateTransform attributeName="transform" type="rotate" from="90 20 20" to="450 20 20" dur="3s" repeatCount="indefinite"/>
      </circle>
      
      <!-- Letter R with glow -->
      <text x="20" y="24" text-anchor="middle" font-size="9" font-weight="bold" fill="#ffffff" filter="url(#cosmicGlow)">R</text>
    </svg>`
  },
  {
    id: "flame",
    name: "Alev",
    svg: `<svg viewBox="0 0 40 40" xmlns="http://www.w3.org/2000/svg">
      <defs>
        <radialGradient id="flameCore" cx="50%" cy="70%">
          <stop offset="0%" style="stop-color:#ffffff;stop-opacity:1" />
          <stop offset="20%" style="stop-color:#ffeb3b;stop-opacity:1" />
          <stop offset="50%" style="stop-color:#ff9800;stop-opacity:0.9" />
          <stop offset="80%" style="stop-color:#ff5722;stop-opacity:0.7" />
          <stop offset="100%" style="stop-color:#b71c1c;stop-opacity:0" />
        </radialGradient>
        <filter id="flameBlur">
          <feGaussianBlur stdDeviation="1.5" result="blur"/>
          <feComposite in="SourceGraphic" in2="blur" operator="over"/>
        </filter>
      </defs>
      
      <!-- Outer flame gradient -->
      <ellipse cx="20" cy="28" rx="8" ry="12" fill="url(#flameCore)" opacity="0.6" filter="url(#flameBlur)">
        <animate attributeName="ry" values="12;13;11;12" dur="1.2s" repeatCount="indefinite"/>
        <animate attributeName="opacity" values="0.5;0.7;0.5" dur="1.2s" repeatCount="indefinite"/>
      </ellipse>
      
      <!-- Middle flame -->
      <ellipse cx="20" cy="26" rx="6" ry="10" fill="url(#flameCore)" opacity="0.8" filter="url(#flameBlur)">
        <animate attributeName="ry" values="10;11;9;10" dur="0.9s" repeatCount="indefinite"/>
      </ellipse>
      
      <!-- Inner bright core -->
      <ellipse cx="20" cy="24" rx="4" ry="7" fill="#ffeb3b" opacity="0.9">
        <animate attributeName="ry" values="7;8;6;7" dur="0.6s" repeatCount="indefinite"/>
      </ellipse>
      
      <!-- Hot center -->
      <ellipse cx="20" cy="22" rx="2.5" ry="4" fill="#ffffff" opacity="0.8">
        <animate attributeName="ry" values="4;5;3.5;4" dur="0.5s" repeatCount="indefinite"/>
      </ellipse>
      
      <!-- Flickering top -->
      <path d="M20,8 Q18,12 20,15 Q22,12 20,8" fill="#ff9800" opacity="0.7">
        <animate attributeName="opacity" values="0.5;0.9;0.4;0.8;0.5" dur="0.3s" repeatCount="indefinite"/>
        <animate attributeName="d" values="M20,8 Q18,12 20,15 Q22,12 20,8;M20,7 Q18,11 20,14 Q22,11 20,7;M20,9 Q18,13 20,16 Q22,13 20,9;M20,8 Q18,12 20,15 Q22,12 20,8" dur="0.4s" repeatCount="indefinite"/>
      </path>
      
      <!-- Ember particles rising -->
      <circle cx="18" cy="10" r="0.5" fill="#ff9800">
        <animate attributeName="cy" values="10;4;10" dur="2s" repeatCount="indefinite"/>
        <animate attributeName="opacity" values="0.8;0;0.8" dur="2s" repeatCount="indefinite"/>
      </circle>
      <circle cx="22" cy="12" r="0.4" fill="#ffeb3b">
        <animate attributeName="cy" values="12;6;12" dur="2.3s" repeatCount="indefinite"/>
        <animate attributeName="opacity" values="0.9;0;0.9" dur="2.3s" repeatCount="indefinite"/>
      </circle>
      <circle cx="20" cy="14" r="0.3" fill="#ffffff">
        <animate attributeName="cy" values="14;8;14" dur="1.8s" repeatCount="indefinite"/>
        <animate attributeName="opacity" values="1;0;1" dur="1.8s" repeatCount="indefinite"/>
      </circle>
      
      <!-- Letter R -->
      <text x="20" y="27" text-anchor="middle" font-size="8" font-weight="bold" fill="#1a1a1a" opacity="0.9">R</text>
    </svg>`
  },
  {
    id: "energy",
    name: "Enerji",
    svg: `<svg viewBox="0 0 40 40" xmlns="http://www.w3.org/2000/svg">
      <defs>
        <radialGradient id="energyCore">
          <stop offset="0%" style="stop-color:#ffffff;stop-opacity:1" />
          <stop offset="30%" style="stop-color:#00ffff;stop-opacity:0.9" />
          <stop offset="70%" style="stop-color:#0080ff;stop-opacity:0.5" />
          <stop offset="100%" style="stop-color:#000080;stop-opacity:0" />
        </radialGradient>
        <filter id="energyGlow">
          <feGaussianBlur stdDeviation="2" result="blur"/>
          <feComposite in="SourceGraphic" in2="blur" operator="over"/>
        </filter>
      </defs>
      
      <!-- Outer energy waves -->
      <circle cx="20" cy="20" r="16" fill="none" stroke="#00ffff" stroke-width="0.8" opacity="0.3">
        <animate attributeName="r" values="16;18;16" dur="1.5s" repeatCount="indefinite"/>
        <animate attributeName="opacity" values="0.3;0.6;0.3" dur="1.5s" repeatCount="indefinite"/>
      </circle>
      <circle cx="20" cy="20" r="13" fill="none" stroke="#00d4ff" stroke-width="1" opacity="0.4">
        <animate attributeName="r" values="13;15;13" dur="1.2s" repeatCount="indefinite"/>
        <animate attributeName="opacity" values="0.4;0.7;0.4" dur="1.2s" repeatCount="indefinite"/>
      </circle>
      <circle cx="20" cy="20" r="10" fill="none" stroke="#0080ff" stroke-width="1.2" opacity="0.5">
        <animate attributeName="r" values="10;12;10" dur="1s" repeatCount="indefinite"/>
        <animate attributeName="opacity" values="0.5;0.8;0.5" dur="1s" repeatCount="indefinite"/>
      </circle>
      
      <!-- Core sphere -->
      <circle cx="20" cy="20" r="7" fill="url(#energyCore)" filter="url(#energyGlow)">
        <animate attributeName="r" values="6.5;7.5;6.5" dur="0.8s" repeatCount="indefinite"/>
      </circle>
      
      <!-- Electric bolts rotating -->
      <g opacity="0.9" filter="url(#energyGlow)">
        <path d="M20,12 L22,18 L19,18 L21,24" stroke="#00ffff" stroke-width="1.5" fill="none" stroke-linecap="round">
          <animateTransform attributeName="transform" type="rotate" from="0 20 20" to="360 20 20" dur="2s" repeatCount="indefinite"/>
        </path>
        <path d="M20,12 L22,18 L19,18 L21,24" stroke="#00ffff" stroke-width="1.5" fill="none" stroke-linecap="round">
          <animateTransform attributeName="transform" type="rotate" from="120 20 20" to="480 20 20" dur="2s" repeatCount="indefinite"/>
        </path>
        <path d="M20,12 L22,18 L19,18 L21,24" stroke="#00ffff" stroke-width="1.5" fill="none" stroke-linecap="round">
          <animateTransform attributeName="transform" type="rotate" from="240 20 20" to="600 20 20" dur="2s" repeatCount="indefinite"/>
        </path>
      </g>
      
      <!-- Letter R -->
      <text x="20" y="24" text-anchor="middle" font-size="8" font-weight="bold" fill="#ffffff" filter="url(#energyGlow)">R</text>
    </svg>`
  },
  {
    id: "nebula",
    name: "Nebula",
    svg: `<svg viewBox="0 0 40 40" xmlns="http://www.w3.org/2000/svg">
      <defs>
        <radialGradient id="nebula1">
          <stop offset="0%" style="stop-color:#ff00ff;stop-opacity:0.9" />
          <stop offset="50%" style="stop-color:#8b00ff;stop-opacity:0.6" />
          <stop offset="100%" style="stop-color:#4b0082;stop-opacity:0" />
        </radialGradient>
        <radialGradient id="nebula2">
          <stop offset="0%" style="stop-color:#00ced1;stop-opacity:0.8" />
          <stop offset="50%" style="stop-color:#0080ff;stop-opacity:0.5" />
          <stop offset="100%" style="stop-color:#000080;stop-opacity:0" />
        </radialGradient>
        <radialGradient id="nebula3">
          <stop offset="0%" style="stop-color:#ff1493;stop-opacity:0.7" />
          <stop offset="50%" style="stop-color:#ff00ff;stop-opacity:0.4" />
          <stop offset="100%" style="stop-color:#8b008b;stop-opacity:0" />
        </radialGradient>
        <filter id="nebulaGlow">
          <feGaussianBlur stdDeviation="2" result="blur"/>
          <feComposite in="SourceGraphic" in2="blur" operator="over"/>
        </filter>
      </defs>
      
      <!-- Nebula clouds - layered -->
      <ellipse cx="14" cy="20" rx="11" ry="14" fill="url(#nebula1)" opacity="0.8">
        <animate attributeName="rx" values="11;12;11" dur="4s" repeatCount="indefinite"/>
        <animate attributeName="ry" values="14;15;14" dur="4s" repeatCount="indefinite"/>
      </ellipse>
      <ellipse cx="26" cy="20" rx="11" ry="14" fill="url(#nebula2)" opacity="0.8">
        <animate attributeName="rx" values="11;12.5;11" dur="3.5s" repeatCount="indefinite"/>
        <animate attributeName="ry" values="14;15.5;14" dur="3.5s" repeatCount="indefinite"/>
      </ellipse>
      <ellipse cx="20" cy="22" rx="8" ry="10" fill="url(#nebula3)" opacity="0.7">
        <animate attributeName="rx" values="8;9;8" dur="3s" repeatCount="indefinite"/>
      </ellipse>
      
      <!-- Stars scattered -->
      <circle cx="11" cy="14" r="1" fill="#ffffff" opacity="0.9">
        <animate attributeName="opacity" values="0.7;1;0.7" dur="2s" repeatCount="indefinite"/>
      </circle>
      <circle cx="29" cy="26" r="0.8" fill="#ffffff" opacity="0.8">
        <animate attributeName="opacity" values="0.6;1;0.6" dur="2.5s" repeatCount="indefinite"/>
      </circle>
      <circle cx="20" cy="10" r="0.7" fill="#ffffff" opacity="0.85">
        <animate attributeName="opacity" values="0.5;1;0.5" dur="1.8s" repeatCount="indefinite"/>
      </circle>
      <circle cx="16" cy="28" r="0.6" fill="#ffffff" opacity="0.8">
        <animate attributeName="opacity" values="0.6;1;0.6" dur="2.2s" repeatCount="indefinite"/>
      </circle>
      <circle cx="26" cy="13" r="0.9" fill="#ffffff" opacity="0.9">
        <animate attributeName="opacity" values="0.7;1;0.7" dur="1.5s" repeatCount="indefinite"/>
      </circle>
      
      <!-- Letter R with glow -->
      <text x="20" y="24" text-anchor="middle" font-size="9" font-weight="bold" fill="#ffffff" filter="url(#nebulaGlow)" opacity="0.95">R</text>
    </svg>`
  },
  {
    id: "quantum",
    name: "Kuantum",
    svg: `<svg viewBox="0 0 40 40" xmlns="http://www.w3.org/2000/svg">
      <defs>
        <radialGradient id="quantumCore">
          <stop offset="0%" style="stop-color:#00ff00;stop-opacity:1" />
          <stop offset="50%" style="stop-color:#00ffff;stop-opacity:0.6" />
          <stop offset="100%" style="stop-color:#0000ff;stop-opacity:0" />
        </radialGradient>
        <filter id="quantumGlow">
          <feGaussianBlur stdDeviation="1.5" result="blur"/>
          <feComposite in="SourceGraphic" in2="blur" operator="over"/>
        </filter>
      </defs>
      
      <!-- Quantum probability rings -->
      <circle cx="20" cy="20" r="15" fill="none" stroke="#00ff00" stroke-width="0.6" opacity="0.4" stroke-dasharray="3,2">
        <animateTransform attributeName="transform" type="rotate" from="0 20 20" to="360 20 20" dur="4s" repeatCount="indefinite"/>
        <animate attributeName="opacity" values="0.3;0.6;0.3" dur="2s" repeatCount="indefinite"/>
      </circle>
      <circle cx="20" cy="20" r="11" fill="none" stroke="#00ffff" stroke-width="0.8" opacity="0.5" stroke-dasharray="2,1">
        <animateTransform attributeName="transform" type="rotate" from="360 20 20" to="0 20 20" dur="3s" repeatCount="indefinite"/>
      </circle>
      
      <!-- Particle wave duality paths -->
      <path d="M20,5 Q15,12 20,20 Q25,28 20,35" fill="none" stroke="#00ff00" stroke-width="0.5" opacity="0.5">
        <animate attributeName="opacity" values="0.3;0.7;0.3" dur="1.5s" repeatCount="indefinite"/>
      </path>
      <path d="M5,20 Q12,15 20,20 Q28,25 35,20" fill="none" stroke="#00ffff" stroke-width="0.5" opacity="0.5">
        <animate attributeName="opacity" values="0.3;0.7;0.3" dur="1.5s" repeatCount="indefinite" begin="0.75s"/>
      </path>
      
      <!-- Orbiting quantum particles -->
      <circle cx="20" cy="7" r="1.8" fill="#00ff00" filter="url(#quantumGlow)">
        <animateTransform attributeName="transform" type="rotate" from="0 20 20" to="360 20 20" dur="2s" repeatCount="indefinite"/>
        <animate attributeName="opacity" values="0.7;1;0.7" dur="1s" repeatCount="indefinite"/>
      </circle>
      <circle cx="20" cy="33" r="1.8" fill="#00ffff" filter="url(#quantumGlow)">
        <animateTransform attributeName="transform" type="rotate" from="180 20 20" to="540 20 20" dur="2s" repeatCount="indefinite"/>
        <animate attributeName="opacity" values="0.7;1;0.7" dur="1s" repeatCount="indefinite"/>
      </circle>
      <circle cx="7" cy="20" r="1.8" fill="#ff00ff" filter="url(#quantumGlow)">
        <animateTransform attributeName="transform" type="rotate" from="90 20 20" to="450 20 20" dur="2s" repeatCount="indefinite"/>
        <animate attributeName="opacity" values="0.7;1;0.7" dur="1s" repeatCount="indefinite"/>
      </circle>
      <circle cx="33" cy="20" r="1.8" fill="#ffff00" filter="url(#quantumGlow)">
        <animateTransform attributeName="transform" type="rotate" from="270 20 20" to="630 20 20" dur="2s" repeatCount="indefinite"/>
        <animate attributeName="opacity" values="0.7;1;0.7" dur="1s" repeatCount="indefinite"/>
      </circle>
      
      <!-- Superposition core -->
      <circle cx="20" cy="20" r="4" fill="url(#quantumCore)" filter="url(#quantumGlow)">
        <animate attributeName="r" values="3.5;4.5;3.5" dur="1.5s" repeatCount="indefinite"/>
      </circle>
      
      <!-- Letter R -->
      <text x="20" y="24" text-anchor="middle" font-size="7" font-weight="bold" fill="#000000" opacity="0.9">R</text>
    </svg>`
  },
  {
    id: "vortex",
    name: "Girdap",
    svg: `<svg viewBox="0 0 40 40" xmlns="http://www.w3.org/2000/svg">
      <defs>
        <radialGradient id="vortexGrad">
          <stop offset="0%" style="stop-color:#000000;stop-opacity:1" />
          <stop offset="40%" style="stop-color:#4b0082;stop-opacity:0.8" />
          <stop offset="70%" style="stop-color:#8b00ff;stop-opacity:0.5" />
          <stop offset="100%" style="stop-color:#ff00ff;stop-opacity:0" />
        </radialGradient>
        <filter id="vortexGlow">
          <feGaussianBlur stdDeviation="2" result="blur"/>
          <feComposite in="SourceGraphic" in2="blur" operator="over"/>
        </filter>
      </defs>
      
      <!-- Outer vortex ring -->
      <circle cx="20" cy="20" r="17" fill="url(#vortexGrad)" opacity="0.6"/>
      
      <!-- Spiral arms rotating -->
      <path d="M20,3 Q28,10 28,20 Q28,30 20,37 Q12,30 12,20 Q12,10 20,3" fill="none" stroke="#ff00ff" stroke-width="1.5" opacity="0.6">
        <animateTransform attributeName="transform" type="rotate" from="0 20 20" to="360 20 20" dur="3s" repeatCount="indefinite"/>
      </path>
      <path d="M37,20 Q30,28 20,28 Q10,28 3,20 Q10,12 20,12 Q30,12 37,20" fill="none" stroke="#00ffff" stroke-width="1.5" opacity="0.6">
        <animateTransform attributeName="transform" type="rotate" from="90 20 20" to="450 20 20" dur="3s" repeatCount="indefinite"/>
      </path>
      
      <!-- Inner spiral -->
      <path d="M20,10 Q25,15 23,20 Q21,25 20,23 Q19,21 20,20 Q21,19 20,18" fill="none" stroke="#8b00ff" stroke-width="1.2" opacity="0.7">
        <animateTransform attributeName="transform" type="rotate" from="0 20 20" to="360 20 20" dur="2s" repeatCount="indefinite"/>
      </path>
      
      <!-- Vortex center -->
      <circle cx="20" cy="20" r="6" fill="#000000" opacity="0.9">
        <animate attributeName="r" values="5.5;6.5;5.5" dur="1.5s" repeatCount="indefinite"/>
      </circle>
      <circle cx="20" cy="20" r="4" fill="#4b0082" opacity="0.7">
        <animate attributeName="r" values="3.5;4.5;3.5" dur="1.2s" repeatCount="indefinite"/>
      </circle>
      
      <!-- Letter R with glow -->
      <text x="20" y="24" text-anchor="middle" font-size="8" font-weight="bold" fill="#ff00ff" filter="url(#vortexGlow)">R</text>
    </svg>`
  },
  {
    id: "galaxy",
    name: "Galaksi",
    svg: `<svg viewBox="0 0 40 40" xmlns="http://www.w3.org/2000/svg">
      <defs>
        <radialGradient id="galaxyCore">
          <stop offset="0%" style="stop-color:#ffd700;stop-opacity:1" />
          <stop offset="50%" style="stop-color:#ffb347;stop-opacity:0.8" />
          <stop offset="100%" style="stop-color:#ff8c00;stop-opacity:0" />
        </radialGradient>
        <filter id="galaxyGlow">
          <feGaussianBlur stdDeviation="1.5" result="blur"/>
          <feComposite in="SourceGraphic" in2="blur" operator="over"/>
        </filter>
      </defs>
      
      <!-- Spiral arms -->
      <ellipse cx="20" cy="20" rx="17" ry="7" fill="none" stroke="#9370db" stroke-width="1.2" opacity="0.5">
        <animateTransform attributeName="transform" type="rotate" from="0 20 20" to="360 20 20" dur="8s" repeatCount="indefinite"/>
      </ellipse>
      <ellipse cx="20" cy="20" rx="15" ry="6" fill="none" stroke="#dda0dd" stroke-width="1" opacity="0.6">
        <animateTransform attributeName="transform" type="rotate" from="0 20 20" to="360 20 20" dur="6s" repeatCount="indefinite"/>
      </ellipse>
      <ellipse cx="20" cy="20" rx="12" ry="5" fill="none" stroke="#ffb6c1" stroke-width="0.8" opacity="0.7">
        <animateTransform attributeName="transform" type="rotate" from="0 20 20" to="360 20 20" dur="4s" repeatCount="indefinite"/>
      </ellipse>
      <ellipse cx="20" cy="20" rx="9" ry="3.5" fill="none" stroke="#ffc0cb" stroke-width="0.6" opacity="0.8">
        <animateTransform attributeName="transform" type="rotate" from="0 20 20" to="360 20 20" dur="3s" repeatCount="indefinite"/>
      </ellipse>
      
      <!-- Galactic core -->
      <circle cx="20" cy="20" r="5" fill="url(#galaxyCore)" filter="url(#galaxyGlow)">
        <animate attributeName="r" values="4.5;5.5;4.5" dur="2s" repeatCount="indefinite"/>
      </circle>
      
      <!-- Stars in spiral arms -->
      <circle cx="13" cy="18" r="0.8" fill="#ffffff" opacity="0.9">
        <animateTransform attributeName="transform" type="rotate" from="0 20 20" to="360 20 20" dur="8s" repeatCount="indefinite"/>
        <animate attributeName="opacity" values="0.7;1;0.7" dur="1.5s" repeatCount="indefinite"/>
      </circle>
      <circle cx="27" cy="22" r="0.7" fill="#ffffff" opacity="0.8">
        <animateTransform attributeName="transform" type="rotate" from="0 20 20" to="360 20 20" dur="8s" repeatCount="indefinite"/>
        <animate attributeName="opacity" values="0.6;1;0.6" dur="2s" repeatCount="indefinite"/>
      </circle>
      <circle cx="18" cy="25" r="0.6" fill="#ffffff" opacity="0.85">
        <animateTransform attributeName="transform" type="rotate" from="0 20 20" to="360 20 20" dur="6s" repeatCount="indefinite"/>
        <animate attributeName="opacity" values="0.7;1;0.7" dur="1.8s" repeatCount="indefinite"/>
      </circle>
      <circle cx="24" cy="15" r="0.7" fill="#ffffff" opacity="0.9">
        <animateTransform attributeName="transform" type="rotate" from="0 20 20" to="360 20 20" dur="6s" repeatCount="indefinite"/>
        <animate attributeName="opacity" values="0.8;1;0.8" dur="1.3s" repeatCount="indefinite"/>
      </circle>
      
      <!-- Letter R -->
      <text x="20" y="23" text-anchor="middle" font-size="7" font-weight="bold" fill="#000000" opacity="0.9">R</text>
    </svg>`
  },
  {
    id: "crystal",
    name: "Kristal",
    svg: `<svg viewBox="0 0 40 40" xmlns="http://www.w3.org/2000/svg">
      <defs>
        <linearGradient id="crystalGrad" x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" style="stop-color:#e0ffff;stop-opacity:0.9" />
          <stop offset="30%" style="stop-color:#00ffff;stop-opacity:0.8" />
          <stop offset="60%" style="stop-color:#0080ff;stop-opacity:0.7" />
          <stop offset="100%" style="stop-color:#4b0082;stop-opacity:0.9" />
        </linearGradient>
        <linearGradient id="crystalHighlight" x1="0%" y1="0%" x2="100%" y2="0%">
          <stop offset="0%" style="stop-color:#ffffff;stop-opacity:0.8" />
          <stop offset="50%" style="stop-color:#00ffff;stop-opacity:0.4" />
          <stop offset="100%" style="stop-color:#ffffff;stop-opacity:0.8" />
        </linearGradient>
        <filter id="crystalGlow">
          <feGaussianBlur stdDeviation="1.5" result="blur"/>
          <feComposite in="SourceGraphic" in2="blur" operator="over"/>
        </filter>
      </defs>
      
      <!-- Crystal facets -->
      <polygon points="20,4 30,12 30,28 20,36 10,28 10,12" fill="url(#crystalGrad)" opacity="0.8" filter="url(#crystalGlow)"/>
      
      <!-- Light refraction facets -->
      <polygon points="20,4 30,12 20,20" fill="#00ffff" opacity="0.5">
        <animate attributeName="opacity" values="0.4;0.6;0.4" dur="2s" repeatCount="indefinite"/>
      </polygon>
      <polygon points="20,20 30,28 20,36" fill="#000080" opacity="0.4">
        <animate attributeName="opacity" values="0.3;0.5;0.3" dur="2s" repeatCount="indefinite" begin="0.5s"/>
      </polygon>
      <polygon points="20,4 10,12 20,20" fill="#e0ffff" opacity="0.5">
        <animate attributeName="opacity" values="0.4;0.6;0.4" dur="2s" repeatCount="indefinite" begin="1s"/>
      </polygon>
      <polygon points="20,20 10,28 20,36" fill="#4b0082" opacity="0.4">
        <animate attributeName="opacity" values="0.3;0.5;0.3" dur="2s" repeatCount="indefinite" begin="1.5s"/>
      </polygon>
      
      <!-- Crystal edges (geometry) -->
      <line x1="20" y1="4" x2="20" y2="36" stroke="#ffffff" stroke-width="0.8" opacity="0.6"/>
      <line x1="10" y1="12" x2="30" y2="28" stroke="#ffffff" stroke-width="0.5" opacity="0.4"/>
      <line x1="30" y1="12" x2="10" y2="28" stroke="#ffffff" stroke-width="0.5" opacity="0.4"/>
      <line x1="10" y1="12" x2="30" y2="12" stroke="#ffffff" stroke-width="0.5" opacity="0.5"/>
      <line x1="10" y1="28" x2="30" y2="28" stroke="#ffffff" stroke-width="0.5" opacity="0.5"/>
      
      <!-- Light beam passing through -->
      <line x1="5" y1="10" x2="15" y2="15" stroke="url(#crystalHighlight)" stroke-width="1.5" opacity="0.6">
        <animate attributeName="opacity" values="0.3;0.8;0.3" dur="2s" repeatCount="indefinite"/>
      </line>
      <line x1="25" y1="25" x2="35" y2="30" stroke="url(#crystalHighlight)" stroke-width="1.5" opacity="0.6">
        <animate attributeName="opacity" values="0.3;0.8;0.3" dur="2s" repeatCount="indefinite" begin="1s"/>
      </line>
      
      <!-- Letter R -->
      <text x="20" y="24" text-anchor="middle" font-size="8" font-weight="bold" fill="#ffffff" filter="url(#crystalGlow)">R</text>
    </svg>`
  },
];

// Theme options
export const themeOptions = [
  { id: "default", name: "Varsayılan", description: "Temiz koyu tema" },
  { id: "cosmic", name: "Kozmik", description: "Yıldızlar ve nebula" },
  { id: "nebulaflow", name: "Nebula Flow", description: "Nebula smoke, glass cards ve akiskan isik izi" },
  { id: "aurora", name: "Aurora", description: "Kuzey ışıkları efekti" },
  { id: "matrix", name: "Matrix", description: "Yeşil kod yağmuru" },
  { id: "neoncity", name: "Neon Şehir", description: "Cyberpunk neon ışıkları" },
  { id: "christmas", name: "Noel", description: "Kar taneleri ve kırmızı-yeşil tema" }
];

// Default site content
const defaultSiteContent = {
  features: [
    { title: "YÜKSEK OTORİTE SİTELER", description: "DA 30+ sitelerden dofollow backlinkler ile domain otoritenizi artırın." },
    { title: "KREDİ SİSTEMİ", description: "Esnek kredi paketleri ile istediğiniz kadar backlink satın alın." },
    { title: "GÜVENLİ ÖDEME", description: "Kripto dahil tüm ödeme yöntemleri ile güvenli işlem yapın." },
    { title: "DOFOLLOW LİNKLER", description: "Tüm backlinklerimiz dofollow özelliğindedir ve SEO değeri taşır." },
    { title: "HIZLI İNDEXLEME", description: "Google'da hızlı indexlenme için optimize edilmiş link yapısı." },
    { title: "7/24 TEKNİK DESTEK", description: "Uzman ekibimiz her zaman yanınızda, sorularınıza anında cevap." }
  ],
  footer: {
    credit: "moderated by AnubisROOT-NoxShell",
    telegramLink: ""
  }
};

export const ThemeProvider = ({ children }) => {
  const [currentTheme, setCurrentTheme] = useState("default");
  const [currentLogo, setCurrentLogo] = useState("default");
  const [siteContent, setSiteContent] = useState(defaultSiteContent);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadSettings();
  }, []);

  useEffect(() => {
    document.documentElement.setAttribute("data-theme", currentTheme);
  }, [currentTheme]);

  const loadSettings = async () => {
    try {
      // Try to load from localStorage first (for immediate display)
      const savedTheme = localStorage.getItem("root-seo-theme");
      const savedLogo = localStorage.getItem("root-seo-logo");
      const savedContent = localStorage.getItem("root-seo-content");
      
      if (savedTheme) setCurrentTheme(normalizeThemeId(savedTheme));
      if (savedLogo) setCurrentLogo(savedLogo);
      if (savedContent) {
        try {
          setSiteContent(JSON.parse(savedContent));
        } catch (e) {}
      }
      
      // First try public settings (works for all users)
      try {
        const publicResponse = await api.get("/public/settings");
        if (publicResponse.data.theme) {
          const normalizedTheme = normalizeThemeId(publicResponse.data.theme);
          setCurrentTheme(normalizedTheme);
          localStorage.setItem("root-seo-theme", normalizedTheme);
        }
        if (publicResponse.data.logo) {
          setCurrentLogo(publicResponse.data.logo);
          localStorage.setItem("root-seo-logo", publicResponse.data.logo);
        }
        if (publicResponse.data.site_content) {
          const content = typeof publicResponse.data.site_content === 'string' 
            ? JSON.parse(publicResponse.data.site_content) 
            : publicResponse.data.site_content;
          setSiteContent({ ...defaultSiteContent, ...content });
          localStorage.setItem("root-seo-content", JSON.stringify(content));
        }
      } catch (e) {
        // Public settings not available
      }
      
      // Then try admin settings only when a session token exists.
      if (localStorage.getItem("token")) {
        try {
          const response = await api.get("/admin/settings");
          if (response.data.theme) {
            const normalizedTheme = normalizeThemeId(response.data.theme);
            setCurrentTheme(normalizedTheme);
            localStorage.setItem("root-seo-theme", normalizedTheme);
          }
          if (response.data.logo) {
            setCurrentLogo(response.data.logo);
            localStorage.setItem("root-seo-logo", response.data.logo);
          }
          if (response.data.site_content) {
            const content = typeof response.data.site_content === 'string' 
              ? JSON.parse(response.data.site_content) 
              : response.data.site_content;
            setSiteContent({ ...defaultSiteContent, ...content });
            localStorage.setItem("root-seo-content", JSON.stringify(content));
          }
        } catch (e) {
          // Not authenticated or no settings - already loaded from public
        }
      }
    } catch (error) {
      console.log("Settings load error:", error);
    } finally {
      setLoading(false);
    }
  };

  const setTheme = async (themeId) => {
    const normalizedTheme = normalizeThemeId(themeId);
    setCurrentTheme(normalizedTheme);
    localStorage.setItem("root-seo-theme", normalizedTheme);
    
    try {
      await api.put("/admin/settings/theme", { value: normalizedTheme });
    } catch (e) {
      // Ignore if not admin
    }
  };

  const setLogo = async (logoId) => {
    setCurrentLogo(logoId);
    localStorage.setItem("root-seo-logo", logoId);
    
    try {
      await api.put("/admin/settings/logo", { value: logoId });
    } catch (e) {
      // Ignore if not admin
    }
  };

  const updateSiteContent = async (newContent) => {
    const updated = { ...siteContent, ...newContent };
    setSiteContent(updated);
    localStorage.setItem("root-seo-content", JSON.stringify(updated));
    
    try {
      await api.put("/admin/settings/site_content", { value: updated });
    } catch (e) {
      // Ignore if not admin
    }
  };

  const getLogo = () => {
    return logoOptions.find(l => l.id === currentLogo) || logoOptions[0];
  };

  return (
    <ThemeContext.Provider value={{ 
      currentTheme, 
      currentLogo, 
      setTheme, 
      setLogo, 
      getLogo,
      loading,
      themeOptions,
      logoOptions,
      siteContent,
      updateSiteContent
    }}>
      {children}
    </ThemeContext.Provider>
  );
};
